import { useContext, useEffect, useState, useRef } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Fade,
  Grid,
  Typography
} from "@mui/material";
import { CustomPrompt } from "components/CustomPrompt";
import ProgressIndicatorContainer from "components/ProgressIndicatorContainer";
import PerfectScrollbar from "react-perfect-scrollbar";
import CaseDetailsForm from "components/CaseManagement/CaseDetail/CaseDetailsForm";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MachineStatusAccordion from "components/CaseManagement/CaseDetail/MachineStatus/MachineStatusAccordion";
import ActionWrapper from "components/CaseManagement/CaseDetail/Action/ActionWrapper";
import cx from "classnames";
import SaveIcon from "@mui/icons-material/Save";
import { styled } from "@mui/material/styles";
import Fab from "@mui/material/Fab";
import PropTypes from "prop-types";
import CaseModelState from "components/CaseManagement/State/CaseModelState";
import useSaveFailureType from "components/CaseManagement/CaseDetail/useSaveFailureType";
import useSaveCaseStatus from "components/CaseManagement/CaseDetail/useSaveCaseStatus";
import { useFetchGetV2 } from "hooks/fetch/useFetchGetV2";
import { isNil } from "lodash";
import { useFetchPatchV2 } from "hooks/fetch/useFetchPatchV2";
import { CaseActionsContext } from "contexts/CaseActionsContext";
import { Breadcrumbs } from "components/Breadcrumbs";
import { Link, useHistory } from "react-router-dom";
import { useMountedState, usePromise } from "react-use";
import { AppTitleContext } from "app_context";
import makeStyles from "@mui/styles/makeStyles";
import styles from "assets/jss/default/views/casePageStyle";
import newCaseStyle from "assets/jss/default/components/newCase";
import commonStyles from "assets/jss/default/commonStyles";
import { useSimilarCasesData } from "components/CaseManagement/SimilarCases/SimilarCasesContext";
import { useQueryClient } from "@tanstack/react-query";
import { AddToProjectDialog } from "components/CaseManagement/CaseForms/AddToProjectDialog";
import { useCaseStore } from "stores/case.store";
import { useShallow } from "zustand/shallow";

const useStyles = makeStyles(styles);
const useCaseDialogStyles = makeStyles(newCaseStyle);
const useCommonStyles = makeStyles(commonStyles);

export function CaseDetailSection({ caseId, onChangeCaseSaving }) {
  const appTitleContext = useContext(AppTitleContext);
  const history = useHistory();
  const classes = useStyles();
  const caseDialogClasses = useCaseDialogStyles();
  const commonClasses = useCommonStyles();
  const mounted = usePromise();
  const isMounted = useMountedState();
  const { fetchSimilarCases } = useSimilarCasesData();
  const queryClient = useQueryClient();

  const previousSearchParams = history.location.state?.search ?? "";
  const cameFromDownDerateTracker =
    history.location.state?.downDerateTrackerSource ?? false;

  const [currentCase, setCurrentCase] = useState(null);
  const [isCaseModelSaved, setIsCaseModelSaved] = useState(true);
  const [showModelSaved, setShowModelSaved] = useState(false);
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [formIsDirty, setFormIsDirty] = useState(false);

  const [addToProjectDialogVisible, setAddToProjectDialogVisible] = useState(
    false
  );

  const isCaseDetailFromValid = useCaseStore(
    useShallow(state => state.isCaseDetailFromValid)
  );

  // set the tab title to the case display id
  useEffect(() => {
    if (currentCase && currentCase.getImmutable().display_id) {
      if (cameFromDownDerateTracker) {
        appTitleContext.setPageTitle(
          <Breadcrumbs>
            <Link to={`/pages/down-derate-tracker?${previousSearchParams}`}>
              Down/Derated Tracker
            </Link>
            <div>{currentCase?.getImmutable().display_id}</div>
          </Breadcrumbs>
        );
      } else {
        appTitleContext.setPageTitle(
          <Breadcrumbs>
            <Link to={`/pages/case-management?${previousSearchParams}`}>
              Case Management
            </Link>
            <div>{currentCase?.getImmutable().display_id}</div>
          </Breadcrumbs>
        );
      }

      document.title = currentCase?.getImmutable().display_id;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCase]);

  useEffect(() => {
    return () => {
      if (
        history.action === "POP" &&
        history.location.pathname === "/pages/case-management"
      ) {
        history.replace(`/pages/case-management?${previousSearchParams}`);
      }
    };
  }, [cameFromDownDerateTracker, history, previousSearchParams]);

  const {
    loading: caseLoading,
    error: caseError,
    response: caseById,
    refetch: refetchCaseById
  } = useFetchGetV2(`cases/${caseId}`, isNil(caseId));

  const { refetchActions } = useContext(CaseActionsContext);

  const {
    saveFailureType,
    createFailureTypeError,
    createFailureTypeLoading
  } = useSaveFailureType();

  const {
    saveCaseStatus,
    caseStatusSaving,
    caseStatusSaveError
  } = useSaveCaseStatus(caseId);

  const {
    loading: updateCaseInProgress,
    error: updateCaseError,
    patchCallback: updateCase
  } = useFetchPatchV2(`cases/${caseId}`, isNil(caseId));

  useEffect(() => {
    onChangeCaseSaving(saveInProgress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveInProgress]);

  // initialise case state
  useEffect(() => {
    if (caseById && !caseLoading && !caseError) {
      const newCase = CaseModelState.createWithContent({
        ...caseById
      });

      setCurrentCase(newCase);
      setIsCaseModelSaved(true);
      setFormIsDirty(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseById, caseLoading, caseError]);

  const formikRef = useRef();

  const onCaseSaveButtonClicked = async () => {
    if (formikRef.current) {
      await formikRef.current.submitForm();
      // After submit, check validity and save
      if (formikRef.current.isValid && !formikRef.current.isSubmitting) {
        const values = formikRef.current.getValues();
        handleSave(values);
      }
    }
  };

  const handleAddToProjectDialogConfirm = async () => {
    try {
      setAddToProjectDialogVisible(false);
      await saveCase();
    } catch (error) {
      console.error(error);
    }
  };

  const handleAddToProjectDialogCancel = () => {
    // reset the project on the case to the initial project
    const newModel = {
      ...currentCase.toJS(),
      project: caseById.project,
      project_name: caseById.project_name
    };

    setCurrentCase(CaseModelState.set(currentCase, newModel));

    setAddToProjectDialogVisible(false);
  };

  const saveCase = async (caseToSave = currentCase) => {
    setIsCaseModelSaved(false); // Always mark as not saved when saving
    setSaveInProgress(true);
    try {
      if (caseToSave.toJS().status !== caseById.status) {
        await mounted(saveCaseStatus(caseToSave.toJS().status));
      }

      const existingCase = caseToSave.toUpdatePayload();

      if (
        existingCase.failure_mode_name !== null &&
        existingCase.failure_mode_name !== caseById.failure_mode_name
      ) {
        existingCase.failure_mode = await saveFailureType(
          existingCase.failure_mode_name
        );
        setCurrentCase(CaseModelState.set(caseToSave, existingCase));
      }

      await mounted(updateCase(existingCase));

      await refetchCaseById();
      await refetchActions();
      await queryClient.invalidateQueries(["streamFeed", caseId]);
      await fetchSimilarCases();

      setIsCaseModelSaved(true);
      setFormIsDirty(false);
      setShowModelSaved(true);
      setTimeout(() => {
        if (!isMounted()) {
          return;
        }
        setShowModelSaved(false);
      }, 5000);
    } catch (e) {
      // ignored
    } finally {
      setSaveInProgress(false);
    }
  };

  const errorNotification = (areaLoading, areaError, message) => {
    const errorExists = areaError && !areaLoading;

    return (
      <div
        className={cx({
          [commonClasses.verticalAlignTextCenter]: errorExists,
          [classes.unsavedChangesNotification]: errorExists,
          [commonClasses.hide]: !errorExists
        })}
      >
        <Fade in={errorExists}>
          <span className={commonClasses.errorText}>{message}</span>
        </Fade>
      </div>
    );
  };

  const anySaveError =
    updateCaseError || caseStatusSaveError || createFailureTypeError;

  const handleSave = updatedModel => {
    const mergedModel = {
      ...caseById, // original case from backend
      ...updatedModel // form values (may be partial)
    };
    const newCase = CaseModelState.createWithContent(mergedModel);
    setCurrentCase(newCase);
    setIsCaseModelSaved(false);
    saveCase(newCase);
  };

  const handleFormDirtyChange = isDirty => {
    setFormIsDirty(isDirty);

    if (isDirty && isCaseModelSaved) {
      setIsCaseModelSaved(false);
    }
  };

  return (
    <>
      <CustomPrompt
        shouldBlockNavigation={!isCaseModelSaved || formIsDirty}
        content="You have unsaved changes in the case details. Are you sure you want to navigate away?"
      />
      {currentCase ? (
        <AddToProjectDialog
          isDialogOpen={addToProjectDialogVisible}
          onConfirm={handleAddToProjectDialogConfirm}
          onClose={handleAddToProjectDialogCancel}
          selectedCases={[caseById]}
          selectedProjectId={currentCase.toJS().project}
        />
      ) : null}
      <Grid item xs={12} md={6} className={classes.detailSection}>
        <ProgressIndicatorContainer loading={caseLoading} error={caseError}>
          {currentCase ? (
            <>
              <div className={caseDialogClasses.dialogWrapper}>
                <PerfectScrollbar>
                  <CaseDetailsForm
                    ref={formikRef}
                    caseModel={currentCase.toJS()}
                    onSave={handleSave}
                    onFormDirtyChange={handleFormDirtyChange}
                  />
                  <MachineStatusAccordion
                    caseId={caseId}
                    machineId={currentCase.toJS().machine}
                  />
                  <Accordion disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography style={{ marginBottom: 0 }} variant="h3">
                        Actions
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container direction="row" alignItems="flex-start">
                        <Grid item xs={12} md={12}>
                          <ActionWrapper caseId={caseId} />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </PerfectScrollbar>
              </div>
              <div className={classes.saveActionContainer}>
                <div
                  className={cx({
                    [commonClasses.textMuted]: !isCaseModelSaved,
                    [commonClasses.verticalAlignTextCenter]: !isCaseModelSaved,
                    [classes.unsavedChangesNotification]: !isCaseModelSaved,
                    [commonClasses.hide]: isCaseModelSaved
                  })}
                >
                  <Fade in={!isCaseModelSaved && !updateCaseError}>
                    <span data-qa="CP-Text-Save-Changes">
                      {saveInProgress
                        ? "Saving..."
                        : "You have unsaved changes."}
                    </span>
                  </Fade>
                </div>
                {errorNotification(
                  updateCaseInProgress,
                  updateCaseError,
                  "Failed to save the case."
                )}
                {errorNotification(
                  createFailureTypeLoading,
                  createFailureTypeError,
                  "Failed to save the new Failure Type."
                )}
                {errorNotification(
                  caseStatusSaving,
                  caseStatusSaveError,
                  "Failed to update case status."
                )}
                <div
                  className={cx({
                    [commonClasses.verticalAlignTextCenter]: showModelSaved,
                    [classes.unsavedChangesNotification]: showModelSaved,
                    [commonClasses.hide]: !showModelSaved
                  })}
                >
                  <Fade in={showModelSaved}>
                    <span
                      className={commonClasses.successText}
                      data-qa="CP-Text-Saved-Sucessful"
                    >
                      {anySaveError
                        ? "Case partially saved."
                        : "Successfully saved."}
                    </span>
                  </Fade>
                </div>
                <Fade
                  in={!caseLoading && !saveInProgress}
                  {...(!caseLoading && !saveInProgress
                    ? { timeout: 1000 }
                    : {})}
                >
                  <CaseInfoSaveButton
                    id="case-info-save-button"
                    color="primary"
                    aria-label="save"
                    data-qa="CP-Button-Save"
                    disabled={saveInProgress || !isCaseDetailFromValid}
                    onClick={onCaseSaveButtonClicked}
                  >
                    <SaveIcon />
                  </CaseInfoSaveButton>
                </Fade>
              </div>
            </>
          ) : !caseById ? (
            <Grid container justifyContent="center" alignItems="center">
              <Grid item className={classes.caseNotFoundItem}>
                Case not found. Please double check the case ID and try again
              </Grid>
            </Grid>
          ) : (
            <div />
          )}
        </ProgressIndicatorContainer>
      </Grid>
    </>
  );
}

const CaseInfoSaveButton = styled(Fab)`
  margin-left: auto;
  bottom: ${({ theme }) => theme.spacing(-1)};
  right: ${({ theme }) => theme.spacing(1)};
  transition: ${({ theme }) =>
    theme.transitions.create(["background", "transform"], {
      duration: theme.transitions.duration.complex
    })};

  &:hover {
    transform: scale(1.1);
  }
`;

CaseDetailSection.propTypes = {
  onChangeCaseSaving: PropTypes.func,
  caseId: PropTypes.number
};
